import { motion, Variants } from 'framer-motion'

export const ProcessSection = () => {
  const steps = [
    {
      number: 1,
      title: 'Design Your Barriers',
      text: `We help you create challenges that require genuine effort: wait 7 days, climb a mountain, go for a walk in the woods. These aren't just obstacles, they're cooling-off periods that let your rational mind override impulse.
     
     Choose how many challenges you must complete. More barriers mean stronger protection.`,
      image: '/images/lock-chest',
      alt: 'A person designing their quests on a fantasy-style map'
    },
    {
      number: 2,
      title: 'Lock Away Access',
      text: `Generate a strong password and update your account. When you activate the lock, your password gets cryptographically shattered using proven algorithms trusted by governments and banks.
     
     This is permanent until you complete your challenges. It is impossible to reconstruct your password without the required pieces.`,
      image: '/images/confirm-lock',
      alt: 'A glowing key being placed into a magical chest, symbolizing the locking of a password'
    },
    {
      number: 3,
      title: 'Earn Back Access',
      text: `When you genuinely need your account (not just for mindless scrolling), complete the challenges you set. Climb that mountain. Wait out the timer. Have a peaceful walk in the woods.
     
     Each completed challenge unlocks one piece of your password. Gather enough pieces and your access is restored.`,
      image: '/images/quest-map',
      alt: 'A person on an adventure, viewing a map with quest locations'
    }
  ]

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.4
      }
    }
  }

  const headerVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: 'easeOut'
      }
    }
  }

  const stepVariants: Variants = {
    hidden: {
      opacity: 0,
      x: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 1.0,
        ease: 'easeOut'
      }
    }
  }

  const numberVariants: Variants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
        delay: 0.4
      }
    }
  }

  const imageVariants: Variants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: 0.6
      }
    }
  }

  return (
    <section className="bg-shire-light relative overflow-hidden pt-20 md:py-40">
      <div className="absolute top-0 left-1/2 z-10 h-full -translate-x-1/2">
        <svg
          className="h-full w-1"
          viewBox="0 0 2 100"
          preserveAspectRatio="none"
        >
          <motion.g
            initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
            whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            viewport={{ once: true }}
            transition={{ duration: 5, ease: 'easeInOut' }}
          >
            <path
              d="M1 0 L1 100"
              stroke="rgb(139 69 19 / 0.5)"
              strokeWidth="2"
              strokeDasharray="1 1"
              fill="none"
              strokeLinecap="butt"
            />
          </motion.g>
        </svg>
      </div>
      <div className="absolute inset-0"></div>
      <motion.div
        className="relative z-10 mx-auto max-w-5xl px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={containerVariants}
      >
        <motion.div
          className="mb-16 text-center backdrop-blur-lg"
          variants={headerVariants}
        >
          <h2 className="font-heading text-shire-dark mb-6 text-4xl md:text-5xl">
            Your Path to Freedom in Three Steps
          </h2>
          <p className="text-shire-bark mx-auto max-w-3xl text-lg leading-relaxed">
            Breaking free from social media addiction takes more than willpower.
            Here&apos;s how to forge unbreakable quest-locks.
          </p>
        </motion.div>

        <div className="relative block md:hidden">
          <div className="relative">
            <div className="relative z-10 space-y-8">
              {steps.map((step, i) => (
                <motion.div
                  key={step.number}
                  className="relative"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={stepVariants}
                >
                  <motion.div
                    className="mb-4 flex justify-center"
                    variants={numberVariants}
                  >
                    <div className="border-shire-bark bg-shire-sun font-heading text-shire-dark z-30 flex size-12 items-center justify-center rounded-full border-2 text-xl font-bold shadow-lg">
                      {step.number}
                    </div>
                  </motion.div>

                  <div className="relative mx-4 mt-8">
                    <motion.div
                      className="border-shire-bark/50 bg-shire-light mx-auto rounded-xl border p-6 shadow-xl backdrop-blur-md"
                      whileHover={{ y: -5 }}
                      transition={{ duration: 0.4 }}
                    >
                      <h3 className="font-heading text-shire-dark mb-4 text-center text-2xl">
                        {step.title}
                      </h3>
                      <p className="text-shire-bark mb-6 text-center text-sm leading-relaxed">
                        {step.text}
                      </p>
                      <motion.div
                        className="relative overflow-hidden rounded-lg shadow-lg"
                        variants={imageVariants}
                      >
                        <div className="aspect-[3/2] w-full">
                          <img
                            src={`${step.image}-348.webp`}
                            srcSet={`${step.image}-348.webp 348w,${step.image}-768.webp 768w`}
                            alt={step.alt}
                            className="size-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width="348"
                            height="232"
                          />
                        </div>
                        <div className="from-shire-dark/20 pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent" />
                      </motion.div>
                    </motion.div>
                  </div>

                  {i < steps.length - 1 && <div className="h-16" />}
                </motion.div>
              ))}
              <div className="h-20" />
            </div>
          </div>
        </div>

        <div className="hidden space-y-20 md:block">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              className={`relative flex flex-col items-center gap-8 md:gap-12 ${
                i % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'
              }`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stepVariants}
            >
              <motion.div
                className="absolute top-6 left-[50%] hidden -translate-x-1/2 -translate-y-1/2 md:block"
                variants={numberVariants}
              >
                <div className="border-shire-bark bg-shire-sun font-heading text-shire-dark flex size-14 items-center justify-center rounded-full border-4 text-2xl font-bold shadow-lg">
                  {step.number}
                </div>
              </motion.div>
              <motion.div
                className={`flex-1 ${i % 2 === 0 ? 'md:pr-16' : 'md:pl-16'}`}
                variants={{
                  hidden: { opacity: 0, x: i % 2 === 0 ? -30 : 30 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: {
                      duration: 1.0,
                      ease: 'easeOut',
                      delay: 0.4
                    }
                  }
                }}
              >
                <h3 className="font-heading text-shire-dark mb-4 text-2xl md:text-3xl">
                  {step.title}
                </h3>
                <p className="text-shire-bark leading-relaxed whitespace-pre-line">
                  {step.text}
                </p>
              </motion.div>
              <motion.div className="relative flex-1" variants={imageVariants}>
                <motion.div
                  className="overflow-hidden rounded-xl shadow-xl"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="aspect-[3/2] w-full">
                    <img
                      src={`${step.image}-348.webp`}
                      srcSet={`${step.image}-348.webp 348w,${step.image}-768.webp 768w`}
                      alt={step.alt}
                      className="size-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width="348"
                      height="232"
                    />
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  )
}
